import { useAtomValue } from "jotai";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { canv } from "@/canv/global";
import { HStack } from "@/shared/ui/Stack";
import { widgetMenuAvailableOptions, widgetMenuCoordinatesAtom } from "../AppStore";
import { calculateMenuCoordinates } from "./lib";
import { useDelegatedMenuTooltips } from "./lib/hooks/useDelegateMenuTooltips";
import type { ActiveSubMenuState, SetActiveSubMenu } from "./types";
import { AiAssistantMenu } from "./ui/AiAssistantMenu/AiAssistantMenu";
import { AlignMenu } from "./ui/AlignMenu/AlignMenu";
import { ArrowEndMenu } from "./ui/ArrowEndMenu/ArrowEndMenu";
import { BorderMenu } from "./ui/BorderMenu/BorderMenu";
import { ColorMenu } from "./ui/ColorMenu/ColorMenu";
import { DocumentPreviewPageMenu } from "./ui/DocumentPreviewPageMenu/DocumentPreviewPageMenu";
import { FilterMenu } from "./ui/FilterMenu/FilterMenu";
import { LineTypeMenu } from "./ui/LineTypeMenu/LineTypeMenu";
import { MenuOption } from "./ui/MenuOption/MenuOption";
import { MindMapDirectionMenu } from "./ui/MindMapDirectionMenu/MindMapDirectionMenu";
import { ShapeMenu } from "./ui/ShapeMenu/ShapeMenu";
import { StickerColorMenu } from "./ui/StickerColorMenu/StickerColorMenu";
import { TextAlignMenu } from "./ui/TextAlignMenu/TextAlignMenu";
import { TextFormatMenu } from "./ui/TextFormatMenu/TextFormatMenu";
import { TextSizeMenu } from "./ui/TextSizeMenu/TextSizeMenu";
import styles from "./WidgetMenu.module.css";

function renderSubmenu(activeSubMenu: ActiveSubMenuState, setActiveSubMenu: SetActiveSubMenu) {
    switch (activeSubMenu.name) {
        case "color":
        case "textColor":
            return <ColorMenu activeSubMenu={activeSubMenu} setActiveSubMenu={setActiveSubMenu} />;

        case "stickerColor":
            return (
                <StickerColorMenu
                    activeSubMenu={activeSubMenu}
                    setActiveSubMenu={setActiveSubMenu}
                />
            );

        case "textAlign":
            return (
                <TextAlignMenu activeSubMenu={activeSubMenu} setActiveSubMenu={setActiveSubMenu} />
            );

        case "textSize":
            return (
                <TextSizeMenu activeSubMenu={activeSubMenu} setActiveSubMenu={setActiveSubMenu} />
            );

        case "filter":
            return <FilterMenu activeSubMenu={activeSubMenu} setActiveSubMenu={setActiveSubMenu} />;

        case "align":
            return <AlignMenu activeSubMenu={activeSubMenu} setActiveSubMenu={setActiveSubMenu} />;

        case "shape":
            return <ShapeMenu activeSubMenu={activeSubMenu} setActiveSubMenu={setActiveSubMenu} />;

        case "border":
            return <BorderMenu activeSubMenu={activeSubMenu} />;

        case "arrowEnd":
        case "arrowEndFirst":
            return (
                <ArrowEndMenu activeSubMenu={activeSubMenu} setActiveSubMenu={setActiveSubMenu} />
            );

        case "lineType":
            return <LineTypeMenu activeSubMenu={activeSubMenu} />;

        case "documentPreviewPage":
            return (
                <DocumentPreviewPageMenu
                    activeSubMenu={activeSubMenu}
                    setActiveSubMenu={setActiveSubMenu}
                />
            );

        case "mindMapDirection":
            return (
                <MindMapDirectionMenu
                    activeSubMenu={activeSubMenu}
                    setActiveSubMenu={setActiveSubMenu}
                />
            );

        case "aiAssistant":
            return (
                <AiAssistantMenu
                    activeSubMenu={activeSubMenu}
                    setActiveSubMenu={setActiveSubMenu}
                />
            );

        case "textFormat":
            return (
                <TextFormatMenu activeSubMenu={activeSubMenu} setActiveSubMenu={setActiveSubMenu} />
            );

        default:
            return null;
    }
}

export function WidgetMenu() {
    const menuRef = useRef<HTMLDivElement>(null);

    const coordinates = useAtomValue(widgetMenuCoordinatesAtom);
    const availableOptions = useAtomValue(widgetMenuAvailableOptions);

    const [activeSubMenu, setActiveSubMenu] = useState<ActiveSubMenuState | null>(null);
    const [isPositioning, setIsPositioning] = useState(false);

    const isMenuOpen = Boolean(coordinates);
    const menuContextKey = coordinates?.selectionKey ?? null;

    // biome-ignore lint/correctness/useExhaustiveDependencies: <Эта логика используется для корректного выставления координат меню после изменений опций, поэтому зависимость только от них>
    useLayoutEffect(() => {
        if (!coordinates || !menuRef.current) {
            return;
        }

        let raf = 0;
        let cancelled = false;

        const menu = menuRef.current;

        const apply = () => {
            if (!menuRef.current || !coordinates) {
                return;
            }

            const menuRect = menu.getBoundingClientRect();
            const canvasRect = canv.canvas.getBoundingClientRect();

            const { top, left } = calculateMenuCoordinates(coordinates, menuRect, canvasRect);

            menuRef.current.style.transform = `translate3d(${left}px, ${top}px, 0)`;
        };

        setIsPositioning(true);

        apply();

        raf = requestAnimationFrame(() => {
            if (cancelled) {
                return;
            }
            apply();
            setIsPositioning(false);
        });

        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
        };
    }, [availableOptions]);

    useEffect(() => {
        if (!coordinates || !menuRef.current) {
            return;
        }

        const menu = menuRef.current;
        const menuRect = menu.getBoundingClientRect();
        const canvasRect = canv.canvas.getBoundingClientRect();

        const { top, left } = calculateMenuCoordinates(coordinates, menuRect, canvasRect);

        menu.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    }, [coordinates]);

    useDelegatedMenuTooltips(menuRef);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <Логика для закрытия подменю в случаях выбора другого виджета/ячейки и т.п., так же при закрытии основного меню>
    useEffect(() => {
        setActiveSubMenu(null);
    }, [menuContextKey]);

    return (
        <>
            <HStack
                className={styles.wrapper}
                gap={2}
                style={{
                    opacity: isMenuOpen ? 1 : 0,
                    visibility: isMenuOpen && !isPositioning ? "visible" : "hidden",
                    marginTop: isMenuOpen ? 0 : 10,
                }}
                ref={menuRef}
            >
                {availableOptions.map((name) => {
                    return (
                        <MenuOption
                            key={name}
                            name={name}
                            activeSubMenu={activeSubMenu}
                            setActiveSubMenu={setActiveSubMenu}
                        />
                    );
                })}
            </HStack>
            {activeSubMenu && renderSubmenu(activeSubMenu, setActiveSubMenu)}
        </>
    );
}
